import { useEffect, useMemo, useState } from "react";
import { categories, feedItems, type CategoryKey, type FeedItem } from "@/mocks/data";

const sortOptions = ["综合", "最新发布", "讨论最多"] as const;
type SortKey = (typeof sortOptions)[number];

const catTag = (key: CategoryKey): string => {
  const hit = categories.find((c) => c.key === key);
  return hit ? hit.name : "热点";
};

function timeLabel(item: FeedItem): string {
  if (item.minutes <= 5) return "刚刚更新";
  if (item.minutes < 60) return `${item.minutes} 分钟前`;
  return `${Math.round(item.minutes / 60)} 小时前`;
}

export default function HotFeed() {
  const [activeCat, setActiveCat] = useState<CategoryKey | "hot">("hot");
  const [activeSort, setActiveSort] = useState<SortKey>("综合");
  const [keyword, setKeyword] = useState("");
  const [selected, setSelected] = useState<FeedItem | null>(null);

  const results = useMemo(() => {
    let list = feedItems;
    if (activeCat !== "hot") list = list.filter((i) => i.cat === activeCat);
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      list = list.filter(
        (i) => i.title.toLowerCase().includes(kw) || i.summary.toLowerCase().includes(kw) || catTag(i.cat).includes(kw),
      );
    }
    const sorted = [...list];
    if (activeSort === "最新发布") sorted.sort((a, b) => a.minutes - b.minutes);
    if (activeSort === "讨论最多") sorted.sort((a, b) => toNum(b.comments) - toNum(a.comments));
    return sorted;
  }, [activeCat, activeSort, keyword]);

  const now = new Date();
  const clock = now.toLocaleTimeString("zh-CN", { hour12: false });

  // lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selected]);

  return (
    <section id="home" className="scroll-mt-28" aria-label="热点信息流">
      {/* feed head */}
      <div className="rounded-2xl border border-background-200/70 bg-background-50 p-4 md:p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 md:text-3xl">正在发生</h1>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-800">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-500" />
              </span>
              实时更新 · {clock}
            </span>
          </div>

          <div className="relative w-full md:w-72">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索热点关键词…"
              aria-label="搜索热点关键词"
              className="w-full rounded-full border border-background-200 bg-background-100/60 py-2 pl-9 pr-9 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => setKeyword("")}
                aria-label="清空搜索"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-primary-600"
              >
                <i className="ri-close-circle-fill" />
              </button>
            )}
          </div>
        </div>

        {/* category tabs */}
        <div className="mt-4 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
          {[{ key: "hot", name: "全站热榜", icon: "ri-fire-fill" } as unknown as CategoryDef, ...categories].map((c) => {
            const isActive = activeCat === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setActiveCat(c.key)}
                className={`shrink-0 rounded-full px-1 py-1 transition-colors ${
                  isActive ? "bg-primary-50 text-primary-700" : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                <span
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium ${
                    isActive ? "bg-primary-500 text-background-50" : "bg-transparent"
                  }`}
                >
                  <i className={c.icon} />
                  {c.name}
                </span>
              </button>
            );
          })}
        </div>

        {/* sort row */}
        <div className="mt-3 flex items-center justify-between border-t border-background-100 pt-3">
          <div className="flex items-center gap-1">
            {sortOptions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setActiveSort(s)}
                className={`whitespace-nowrap rounded-full px-1 py-1 ${
                  activeSort === s ? "text-foreground-900" : "text-foreground-500"
                }`}
              >
                <span
                  className={`block rounded-full px-3 py-1 text-xs transition-colors ${
                    activeSort === s ? "bg-background-200 font-semibold text-foreground-900" : "hover:text-foreground-700"
                  }`}
                >
                  {s}
                </span>
              </button>
            ))}
          </div>
          <span className="hidden text-xs text-foreground-400 sm:block">
            共 <span className="font-semibold text-foreground-600">{results.length}</span> 条热点正在跟进
          </span>
        </div>
      </div>

      {/* feed list */}
      <div className="mt-4 space-y-3">
        {results.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-200 bg-background-50 py-16 text-center">
            <i className="ri-folder-open-line text-4xl text-foreground-300" />
            <p className="mt-3 text-sm font-medium text-foreground-600">没有找到相关热点</p>
            <p className="mt-1 text-xs text-foreground-400">换个关键词或切换栏目试试吧</p>
          </div>
        )}

        {results.map((item, idx) => {
          const hasImg = !!item.img;
          return (
            <article
              key={item.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelected(item)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setSelected(item);
              }}
              className={`group cursor-pointer overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-colors hover:border-primary-200 ${
                hasImg ? "flex flex-col" : "p-4 md:p-5"
              }`}
            >
              {hasImg ? (
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-56">
                    <img
                      src={item.img}
                      alt={item.imgAlt}
                      className="h-40 w-full object-cover object-top sm:h-full sm:max-h-[10rem]"
                      width={420}
                      height={300}
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-4 md:p-5">{renderMeta(item, idx, true)}</div>
                </div>
              ) : (
                renderMeta(item, idx, false)
              )}
            </article>
          );
        })}
      </div>

      {selected && <EventModal item={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}

// local type for the tab used in map
interface CategoryDef {
  key: string;
  name: string;
  icon: string;
}

function toNum(v: string): number {
  return Number(v.replace("万", "0000").replace(/,/g, ""));
}

function renderMeta(item: FeedItem, idx: number, withImage: boolean) {
  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${
            idx % 2 === 0 ? "bg-primary-50 text-primary-700" : "bg-accent-50 text-accent-700"
          }`}
        >
          {idx === 0 && <i className="ri-fire-fill" />}
          {catTag(item.cat)}
        </span>
        {item.urgent && (
          <span className="inline-flex items-center gap-1 rounded bg-secondary-100 px-1.5 py-0.5 text-[11px] font-semibold text-secondary-800">
            <i className="ri-flashlight-fill" />
            急速更新
          </span>
        )}
        <span className="inline-flex items-center gap-1 text-[11px] text-foreground-400">
          <i className="ri-time-line" />
          {timeLabel(item)}
        </span>
      </div>

      <h3
        className={`mt-2 font-heading font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700 ${
          withImage ? "text-base md:text-lg" : "text-base"
        }`}
      >
        <span className="line-clamp-2">{item.title}</span>
      </h3>

      <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>

      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-4 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-fire-line text-primary-500" />
            热度 {item.heat}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-chat-3-line" />
            {item.comments}
          </span>
          <span className="hidden items-center gap-1 sm:inline-flex">
            <i className="ri-heart-3-line" />
            {item.likes}
          </span>
        </div>
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-primary-600">
          查看进展
          <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </div>
  );
}

const timelineExtras = [
  "首发消息流出，相关词条迅速登上多个平台热榜",
  "多家媒体跟进报道，话题阅读量持续攀升",
  "当事方/官方平台作出回应，事件脉络逐渐清晰",
  "讨论热度进入稳定期，编辑部持续滚动补充最新进展",
];

function EventModal({ item, onClose }: { item: FeedItem; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center p-0 sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-label="事件进展详情">
      <div className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 max-h-[86vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-background-200 bg-background-50 sm:rounded-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-background-100 bg-background-50/95 px-5 py-3.5 backdrop-blur">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-primary-700">
            <i className="ri-focus-2-line" />
            正在滚动跟进
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭详情"
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-5 md:p-6">
          {item.img && (
            <img
              src={item.img}
              alt={item.imgAlt}
              className="mb-4 h-48 w-full rounded-xl object-cover object-top md:h-60"
              width={420}
              height={300}
            />
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded bg-primary-50 px-2 py-0.5 font-semibold text-primary-700">{catTag(item.cat)}</span>
            <span className="inline-flex items-center gap-1 text-foreground-400">
              <i className="ri-time-line" />
              {timeLabel(item)}
            </span>
            <span className="inline-flex items-center gap-1 text-foreground-400">
              <i className="ri-fire-line" />
              热度 {item.heat}
            </span>
          </div>

          <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-900 md:text-2xl">
            {item.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.summary}</p>

          <div className="mt-5 rounded-xl bg-background-100/70 p-4">
            <h4 className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800">
              <i className="ri-time-line text-primary-500" />
              事件进展时间线
            </h4>
            <ol className="mt-3 space-y-0">
              {[item.summary, ...timelineExtras].map((t, i) => (
                <li key={i} className="relative flex gap-3 pb-4 last:pb-0">
                  {i < timelineExtras.length ? (
                    <>
                      <span className="mt-1 flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-full border-2 border-primary-400 bg-background-50" />
                      <span className="absolute left-[4px] top-4 h-full w-px bg-primary-200" />
                    </>
                  ) : (
                    <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-accent-400" />
                  )}
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-foreground-400">
                      {i === 0 ? timeLabel(item) : `进展 ${i} · 持续更新`}
                    </p>
                    <p className="mt-0.5 text-sm leading-relaxed text-foreground-700">{t}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-xs text-foreground-500">
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-full bg-background-100 px-3 py-1.5 font-medium text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
              >
                <i className="ri-chat-3-line" />
                {item.comments} 讨论
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-full bg-background-100 px-3 py-1.5 font-medium text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
              >
                <i className="ri-share-forward-line" />
                转发进展
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              知道了，去热榜看看
              <i className="ri-arrow-right-up-line" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}